import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Loader2, User, Car, Home, Heart, Briefcase, Zap, Phone, ChevronRight, Copy, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dmy = (iso) => {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

const ASSET_META = {
  auto: { icon: Car, label: 'Veicolo' },
  moto: { icon: Car, label: 'Moto' },
  casa: { icon: Home, label: 'Casa' },
  salute: { icon: Heart, label: 'Salute' },
  attivita: { icon: Briefcase, label: 'Attività' },
  utenze: { icon: Zap, label: 'Utenza' },
  telefonia: { icon: Phone, label: 'Telefonia' },
};

const assetLine = (a) => {
  if (a.asset_type === 'auto' || a.asset_type === 'moto')
    return `${a.marca || ''} ${a.modello || ''} ${a.targa ? '· ' + a.targa : ''}`.trim() || 'Veicolo';
  if (a.asset_type === 'casa')
    return [a.indirizzo, a.city].filter(Boolean).join(', ') || 'Casa';
  if (a.asset_type === 'attivita')
    return a.ragione_sociale || a.tipo_attivita || 'Attività';
  if (a.asset_type === 'salute')
    return `${(a.persone_assicurate || []).length} persona/e assicurate`;
  if (a.asset_type === 'utenze')
    return `${(a.tipo_utenza || '').toUpperCase()} ${a.fornitore ? '· ' + a.fornitore : ''}`.trim() || 'Utenza';
  if (a.asset_type === 'telefonia')
    return `${a.operatore || ''} ${a.numero_telefono ? '· ' + a.numero_telefono : ''}`.trim() || 'Telefonia';
  return a.asset_type || 'Bene';
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs text-slate-500">{label}</p>
    <p className="font-medium text-slate-900 break-words">{value || '—'}</p>
  </div>
);

const normUrl = (u) => (/^https?:\/\//i.test(u) ? u : `https://${u}`);

export default function ClientPreviewModal({ clientId, open, onClose, assetTypes = null }) {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [revealPwd, setRevealPwd] = useState({});

  const copyText = (txt, label = 'Copiato') => {
    try { navigator.clipboard.writeText(String(txt || '')); toast.success(label); } catch (_) {}
  };

  useEffect(() => {
    if (!open || !clientId) return;
    let alive = true;
    setLoading(true); setError(false); setClient(null); setAssets([]);
    Promise.all([
      axios.get(`${API}/clients/${clientId}`),
      axios.get(`${API}/assets`, { params: { client_id: clientId } }).catch(() => ({ data: [] })),
    ])
      .then(([c, a]) => {
        if (!alive) return;
        setClient(c.data);
        let list = Array.isArray(a.data) ? a.data : [];
        if (assetTypes && assetTypes.length) list = list.filter((x) => assetTypes.includes(x.asset_type));
        setAssets(list);
      })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, clientId, assetTypes]);

  const isCompany = client?.is_company && client?.company_name;
  const nome = client
    ? (isCompany ? client.company_name : `${client.last_name || ''} ${client.first_name || ''}`.trim())
    : '';
  const luogoNascita = client
    ? (client.birth_abroad
        ? (client.birth_country ? `${client.birth_country} (Estero)` : 'Estero')
        : [client.birth_comune, client.birth_provincia && `(${client.birth_provincia})`].filter(Boolean).join(' '))
    : '';
  const sesso = { M: 'Maschio', F: 'Femmina' }[client?.gender] || client?.gender || '';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-slate-500 flex-shrink-0" />
            <button
              type="button"
              onClick={() => { onClose(); navigate(`/clients?open=${clientId}`); }}
              className="flex items-center gap-2 hover:underline text-left"
              title="Apri anagrafica completa"
              data-testid="preview-open-anagrafica"
            >
              <span>{nome || 'Cliente'}</span>
              {client?.client_code && (
                <Badge className="bg-slate-900 text-white font-mono text-xs">{client.client_code}</Badge>
              )}
            </button>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center gap-2 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" /> Caricamento…
          </div>
        ) : error || !client ? (
          <p className="py-10 text-center text-slate-500">Impossibile caricare l'anagrafica.</p>
        ) : (
          <div className="space-y-4 text-sm">
            {isCompany && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                <p className="text-xs text-amber-700">Denominazione</p>
                <p className="font-bold text-amber-900 text-base">{client.company_name}</p>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
              <Field label="Cognome" value={client.last_name} />
              <Field label="Nome" value={client.first_name} />
              <Field label="Sesso" value={sesso} />
              <Field label="Data di Nascita" value={dmy(client.birth_date)} />
              <Field label="Luogo di Nascita" value={luogoNascita} />
              <Field label="Codice Fiscale" value={client.fiscal_code} />
              <Field label="Cellulare" value={client.phone} />
              <Field label="Email" value={client.email} />
              <Field label="WhatsApp" value={client.whatsapp_contact} />
              <Field label="WeChat" value={client.wechat_contact} />
              <Field label="Indirizzo" value={client.address} />
              <Field label="N. Civico" value={client.address_number} />
              <Field label="Città" value={client.city} />
              <Field label="Provincia" value={client.city_provincia} />
              <Field label="CAP" value={client.postal_code} />
              <Field label="Agente / Fonte" value={client.agent_source} />
              {isCompany && (
                <>
                  <Field label="P. IVA" value={client.vat_number} />
                  <Field label="PEC" value={client.pec} />
                </>
              )}
            </div>

            {client.notes && (
              <div className="border-t pt-3">
                <p className="text-xs text-slate-500 mb-1">Note</p>
                <p className="text-slate-700 whitespace-pre-wrap">{client.notes}</p>
              </div>
            )}

            {assetTypes && assetTypes.length === 1 && assetTypes[0] === 'utenze' && assets.length === 0 && (
              <div className="border-t pt-3 text-xs text-slate-400">
                Nessuna utenza registrata come bene per questo cliente.
              </div>
            )}

            {assets.length > 0 && (
              <div className="border-t pt-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  {assetTypes && assetTypes.length === 1 && assetTypes[0] === 'utenze' ? 'Utenze' : 'Beni'} ({assets.length})
                </p>
                <div className="space-y-2">
                  {assets.map((a) => {
                    const meta = ASSET_META[a.asset_type] || { icon: Briefcase, label: a.asset_type };
                    const Icon = meta.icon;
                    const hasPortale = a.asset_type === 'utenze' && (a.portale_url || a.portale_login || a.portale_password);
                    return (
                      <div key={a.id} className="rounded-lg border border-slate-100 bg-slate-50 p-2.5">
                        <div className="flex items-center gap-2 text-slate-700">
                          <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="text-xs text-slate-400 w-16 flex-shrink-0">{meta.label}</span>
                          <span className="font-medium">{assetLine(a)}</span>
                        </div>
                        {a.asset_type === 'utenze' && (a.pod_pdr_codice || a.codice_cliente_utente) && (
                          <div className="ml-[88px] mt-1 text-xs text-slate-500 flex flex-wrap gap-x-4">
                            {a.pod_pdr_codice && <span>Codice: <span className="font-mono text-slate-700">{a.pod_pdr_codice}</span></span>}
                            {a.codice_cliente_utente && <span>Cod. cliente: <span className="font-mono text-slate-700">{a.codice_cliente_utente}</span></span>}
                          </div>
                        )}
                        {hasPortale && (
                          <div className="ml-[88px] mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                            {a.portale_url && (
                              <a href={normUrl(a.portale_url)} target="_blank" rel="noopener noreferrer"
                                 className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white font-medium px-2.5 py-1 rounded-md no-underline">
                                <ChevronRight className="w-3.5 h-3.5" /> Apri portale
                              </a>
                            )}
                            {a.portale_login && (
                              <span className="inline-flex items-center gap-1">
                                <span className="text-slate-500">Login:</span>
                                <span className="font-mono font-medium text-slate-900">{a.portale_login}</span>
                                <button type="button" onClick={() => copyText(a.portale_login, 'Login copiato')} title="Copia login">
                                  <Copy className="w-3 h-3 text-slate-400 hover:text-slate-700" />
                                </button>
                              </span>
                            )}
                            {a.portale_password && (
                              <span className="inline-flex items-center gap-1">
                                <span className="text-slate-500">Password:</span>
                                <span className="font-mono font-medium text-slate-900">
                                  {revealPwd[a.id] ? a.portale_password : '••••••••'}
                                </span>
                                <button type="button" onClick={() => setRevealPwd((p) => ({ ...p, [a.id]: !p[a.id] }))} title="Mostra/Nascondi">
                                  <Eye className="w-3 h-3 text-slate-400 hover:text-slate-700" />
                                </button>
                                <button type="button" onClick={() => copyText(a.portale_password, 'Password copiata')} title="Copia password">
                                  <Copy className="w-3 h-3 text-slate-400 hover:text-slate-700" />
                                </button>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
